import { Image as ImageType } from "@/types/Image";
import Image from "next/image";
import React from "react";
import "./index.scss";

const createImage = (
  image: ImageType,
  rowStart: number,
  rowEnd: number,
  columnStart: number,
  columnEnd: number,
  onClick: () => void,
  isCover = false,
  isHidden = false
) => {
  const numberOfColumns = columnEnd - columnStart;

  return (
    <div
      className={`image-container ${isCover ? "cover" : ""} ${
        isHidden ? "album-image-hidden" : ""
      }`}
      style={{
        gridArea: `${rowStart} / ${columnStart} / ${rowEnd} / ${columnEnd}`,
        paddingTop: isCover ? "0" : `${(image.height / image.width) * 100}%`,
      }}
      key={`${image.src}-${rowStart}-${rowEnd}-${columnStart}-${columnEnd}`}
    >
      <Image
        src={image.src}
        alt={image.alt}
        fill
        sizes={
          numberOfColumns === 2
            ? "(max-width: 900px) 100vw, 900px"
            : "(max-width: 900px) 50vw, 450px"
        }
        priority={true}
        onClick={onClick}
      />
    </div>
  );
};

const Album = React.forwardRef<
  HTMLDivElement,
  {
    images: ImageType[];
    onClick: (imageIndex: number) => void;
  }
>(({ images, onClick }, ref) => {
  let currentRow = 1;
  let isItTimeForLayout1 = true;

  const imageNodes: any[] = [];

  for (let imageIndex = 0; imageIndex < images.length; imageIndex++) {
    const image1 = images[imageIndex];
    const isImage1Landscape = image1.width >= image1.height;
    const image2 = images[imageIndex + 1];
    const isImage2Landscape = image2 && image2.width >= image2.height;
    const image3 = images[imageIndex + 2];
    const isImage3Landscape = image3 && image3.width >= image3.height;

    if (isItTimeForLayout1 && isImage1Landscape) {
      //console.log("Type 1 - it's time!");
      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 1,
          1,
          3,
          onClick.bind(this, imageIndex)
        )
      );
      isItTimeForLayout1 = false;
      currentRow++;
    } else if (image1 && !image2 && !image3) {
      if (isImage1Landscape) {
        //console.log("Type 1a");
        imageNodes.push(
          createImage(
            image1,
            currentRow,
            currentRow + 1,
            1,
            3,
            onClick.bind(this, imageIndex)
          )
        );
        currentRow++;
      } else {
        //console.log("Type 4a");
        imageNodes.push(
          createImage(
            image1,
            currentRow,
            currentRow + 2,
            1,
            2,
            onClick.bind(this, imageIndex)
          )
        );
        isItTimeForLayout1 = true;
        currentRow++;
      }
    } else if (isImage1Landscape && isImage2Landscape) {
      //console.log("Type 2");
      const image1AspectRatio = image1.width / image1.height;
      const image2AspectRatio = image2.width / image2.height;

      let isCoverImage1 = false;
      let isCoverImage2 = false;
      if (image1AspectRatio !== image2AspectRatio) {
        isCoverImage1 = image1.height > image2.height;
        isCoverImage2 = image2.height > image1.height;
      }

      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 1,
          1,
          2,
          onClick.bind(this, imageIndex),
          isCoverImage1
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow,
          currentRow + 1,
          2,
          3,
          onClick.bind(this, imageIndex + 1),
          isCoverImage2
        )
      );

      imageIndex++;
      isItTimeForLayout1 = true;
      currentRow++;
    } else if (!isImage1Landscape && !isImage2Landscape) {
      //console.log("Type 3");
      const image1AspectRatio = image1.width / image1.height;
      const image2AspectRatio = image2.width / image2.height;

      let isCoverImage1 = false;
      let isCoverImage2 = false;
      if (image1AspectRatio !== image2AspectRatio) {
        isCoverImage1 = image1AspectRatio > image2AspectRatio;
        isCoverImage2 = image2AspectRatio > image1AspectRatio;
      }

      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 2,
          1,
          2,
          onClick.bind(this, imageIndex),
          isCoverImage1
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow,
          currentRow + 2,
          2,
          3,
          onClick.bind(this, imageIndex + 1),
          isCoverImage2
        )
      );

      imageIndex++;
      isItTimeForLayout1 = true;
      currentRow = currentRow + 2;
    } else if (image1 && image2 && !image3) {
      //console.log("Type TWO_COLUMNS");
      if (isImage1Landscape) {
        imageNodes.push(
          createImage(
            image1,
            currentRow,
            currentRow + 1,
            1,
            2,
            onClick.bind(this, imageIndex)
          )
        );
        imageNodes.push(
          createImage(
            image1,
            currentRow + 1,
            currentRow + 2,
            1,
            2,
            onClick.bind(this, imageIndex),
            false,
            true
          )
        );
        imageNodes.push(
          createImage(
            image2,
            currentRow,
            currentRow + (isImage2Landscape ? 1 : 2),
            2,
            3,
            onClick.bind(this, imageIndex + 1),
            true
          )
        );
      } else {
        // image 1 is portriat
        imageNodes.push(
          createImage(
            image1,
            currentRow,
            currentRow + 2,
            1,
            2,
            onClick.bind(this, imageIndex),
            true
          )
        );
        imageNodes.push(
          createImage(
            image2,
            currentRow,
            currentRow + 1,
            2,
            3,
            onClick.bind(this, imageIndex + 1)
          )
        );
        imageNodes.push(
          createImage(
            image2,
            currentRow + 1,
            currentRow + 2,
            2,
            3,
            onClick.bind(this, imageIndex + 1),
            false,
            true
          )
        );
      }

      imageIndex++;
      isItTimeForLayout1 = true;
      if (!isImage1Landscape || !isImage2Landscape) {
        currentRow = currentRow + 2;
      } else {
        currentRow++;
      }
    } else if (!isImage1Landscape && isImage2Landscape && isImage3Landscape) {
      //console.log("Type 4b");
      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 2,
          1,
          2,
          onClick.bind(this, imageIndex),
          true
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow,
          currentRow + 1,
          2,
          3,
          onClick.bind(this, imageIndex + 1)
        )
      );
      imageNodes.push(
        createImage(
          image3,
          currentRow + 1,
          currentRow + 2,
          2,
          3,
          onClick.bind(this, imageIndex + 2)
        )
      );

      imageIndex = imageIndex + 2;
      isItTimeForLayout1 = true;
      currentRow = currentRow + 2;
    } else if (isImage1Landscape && isImage2Landscape && !isImage3Landscape) {
      //console.log("Type 5");
      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 1,
          1,
          2,
          onClick.bind(this, imageIndex)
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow + 1,
          currentRow + 2,
          1,
          2,
          onClick.bind(this, imageIndex + 1)
        )
      );
      imageNodes.push(
        createImage(
          image3,
          currentRow,
          currentRow + 2,
          2,
          3,
          onClick.bind(this, imageIndex + 2),
          true
        )
      );

      imageIndex = imageIndex + 2;
      isItTimeForLayout1 = true;
      currentRow = currentRow + 2;
    } else if (isImage1Landscape && !isImage2Landscape) {
      //console.log("Type 1b");
      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 1,
          1,
          3,
          onClick.bind(this, imageIndex)
        )
      );

      isItTimeForLayout1 = false;
      currentRow++;
    } else if (!isImage1Landscape && isImage2Landscape) {
      //console.log("Type 4c");
      imageNodes.push(
        createImage(
          image1,
          currentRow,
          currentRow + 2,
          1,
          2,
          onClick.bind(this, imageIndex),
          true
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow,
          currentRow + 1,
          2,
          3,
          onClick.bind(this, imageIndex + 1)
        )
      );
      imageNodes.push(
        createImage(
          image2,
          currentRow + 1,
          currentRow + 2,
          2,
          3,
          onClick.bind(this, imageIndex + 1),
          false,
          true
        )
      );

      imageIndex++;
      isItTimeForLayout1 = true;
      currentRow = currentRow + 2;
    }
  }

  return (
    <div className="component album" ref={ref}>
      {imageNodes}
    </div>
  );
});

Album.displayName = "Album";

export default Album;
